import { useEffect, useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronLeft } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { BottomSheet } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { CtaRow, SheetCtaButton, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import {
  OptionButton,
  OptionsList,
  RadioCircle,
  SheetQuestion,
} from "@acto/taskpane/field-sales-2/components/primitives/SheetOptions";

const REASONS = ["mail", "call", "other"] as const;

export type MarkAsDoneReason = (typeof REASONS)[number];

const SecondaryCtaButton = styled(SheetCtaButton)(({ theme }) => ({
  width: "100%",
  minHeight: 52,
  justifyContent: "center",
  color: theme.fieldSalesTwo.colors.neutral[800],
  fontSize: theme.fieldSalesTwo.font.size[15],
}));

interface MarkAsDoneSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStartRecap: (reason: MarkAsDoneReason) => void;
  onCompleteWithoutRecap: (reason: MarkAsDoneReason) => void;
  isSubmitting?: boolean;
}

/**
 * Feedback step of the mark-as-done flow: asks which action was taken for the customer
 * (single-select reason), then completes either with a recap or without one.
 */
export function MarkAsDoneSheet({
  isOpen,
  onOpenChange,
  onStartRecap,
  onCompleteWithoutRecap,
  isSubmitting,
}: MarkAsDoneSheetProps) {
  const t = useTranslations("fieldSalesTwo.customerActions");
  const [selectedReason, setSelectedReason] = useState<MarkAsDoneReason | null>(null);

  useEffect(() => {
    if (!isOpen) setSelectedReason(null);
  }, [isOpen]);

  const handleComplete = (complete: (reason: MarkAsDoneReason) => void) => {
    if (!selectedReason || isSubmitting) return;
    complete(selectedReason);
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("markAsDone.sheetTitle")}
      closeAriaLabel={t("markAsDone.backAriaLabel")}
      closeIcon={<ChevronLeft />}
    >
      <SheetQuestion id="mark-as-done-question">{t("markAsDone.question")}</SheetQuestion>
      <OptionsList
        value={selectedReason ?? ""}
        onValueChange={(next) => setSelectedReason(next as MarkAsDoneReason)}
        aria-labelledby="mark-as-done-question"
      >
        {REASONS.map((reason) => (
          <OptionButton key={reason} value={reason}>
            {t(`markAsDone.reasons.${reason}`)}
            <RadioCircle aria-hidden="true" />
          </OptionButton>
        ))}
      </OptionsList>
      <CtaRow>
        <SolidCtaButton
          type="button"
          disabled={!selectedReason || isSubmitting}
          onClick={() => handleComplete(onStartRecap)}
        >
          {t("markAsDone.startRecap")}
        </SolidCtaButton>
        <SecondaryCtaButton
          as="button"
          type="button"
          disabled={!selectedReason || isSubmitting}
          onClick={() => handleComplete(onCompleteWithoutRecap)}
        >
          {t("markAsDone.continueWithoutRecap")}
        </SecondaryCtaButton>
      </CtaRow>
    </BottomSheet>
  );
}
